// Service Worker Integration for React App

// 🌐 CONNECTIVITY STATUS GLOBAL TRACKING
let connectivityState = {
  isOnline: navigator.onLine,
  lastUpdate: Date.now(),
  serviceWorkerActive: false,
  // ✅ NOVOS CAMPOS PARA DETECÇÃO DE REDE LENTA
  isSlowNetwork: false,
  connectionSpeed: 'unknown', // 'slow', 'medium', 'fast', 'unknown'
  lastSpeedTest: 0,
  rtt: 0, // Round Trip Time
  downlink: 0, // Download speed
  effectiveType: 'unknown' // Network effective type
};

// 📞 NOTIFICATION CALLBACK SYSTEM
let globalNotificationCallback: Function | null = null;

// 🔧 COMPATIBILITY FUNCTIONS (for existing useConnectivity hook)
export const getConnectivityStatus = () => {
  return {
    isOnline: connectivityState.isOnline,
    lastUpdate: connectivityState.lastUpdate,
    serviceWorkerActive: connectivityState.serviceWorkerActive,
    navigator: navigator.onLine,
    timestamp: Date.now(),
    // ✅ NOVOS CAMPOS PARA REDE LENTA
    isSlowNetwork: connectivityState.isSlowNetwork,
    connectionSpeed: connectivityState.connectionSpeed,
    lastSpeedTest: connectivityState.lastSpeedTest,
    rtt: connectivityState.rtt,
    downlink: connectivityState.downlink,
    effectiveType: connectivityState.effectiveType
  };
};

export const setNotificationCallback = (callback: Function | null) => {
  globalNotificationCallback = callback;
  // console.log('📞 [SW Integration] Notification callback', callback ? 'set' : 'cleared');
};

// 📡 INTERNAL NOTIFICATION SYSTEM
const notifyConnectivityChange = (online: boolean) => {
  connectivityState.isOnline = online;
  connectivityState.lastUpdate = Date.now();
  
  if (globalNotificationCallback) {
    try {
      globalNotificationCallback({
        type: 'connectivity',
        online: online,
        timestamp: connectivityState.lastUpdate
      });
    } catch (error) {
      console.error('❌ [SW Integration] Notification callback error:', error);
    }
  }
};

// ✅ NOVA FUNÇÃO PARA NOTIFICAR MUDANÇAS NA VELOCIDADE DA REDE
const notifyNetworkSpeedChange = (speedInfo: { 
  isSlowNetwork: boolean; 
  connectionSpeed: string; 
  rtt?: number; 
  downlink?: number; 
  effectiveType?: string;
}) => {
  // Atualizar estado
  connectivityState.isSlowNetwork = speedInfo.isSlowNetwork;
  connectivityState.connectionSpeed = speedInfo.connectionSpeed;
  connectivityState.lastSpeedTest = Date.now();
  
  if (speedInfo.rtt !== undefined) connectivityState.rtt = speedInfo.rtt;
  if (speedInfo.downlink !== undefined) connectivityState.downlink = speedInfo.downlink;
  if (speedInfo.effectiveType !== undefined) connectivityState.effectiveType = speedInfo.effectiveType;
  
  if (globalNotificationCallback) {
    try {
      globalNotificationCallback({
        type: 'network_speed',
        isSlowNetwork: speedInfo.isSlowNetwork,
        connectionSpeed: speedInfo.connectionSpeed,
        rtt: speedInfo.rtt,
        downlink: speedInfo.downlink,
        effectiveType: speedInfo.effectiveType,
        timestamp: connectivityState.lastSpeedTest
      });
    } catch (error) {
      console.error('❌ [SW Integration] Network speed notification error:', error);
    }
  }
};

// ✅ FUNÇÃO PARA DETECTAR REDE LENTA
const detectSlowNetwork = () => {
  try {
    // Verificar se a API Network Information está disponível
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    if (connection) {
      const { rtt, downlink, effectiveType } = connection;
      
      // Determinar se a rede é lenta baseado em múltiplos fatores
      let isSlowNetwork = false;
      let connectionSpeed = 'unknown';
      
      // Critérios para rede lenta:
      // - RTT > 500ms OU
      // - Downlink < 1Mbps OU  
      // - effectiveType é 'slow-2g' ou '2g'
      if (rtt > 500 || downlink < 1 || effectiveType === 'slow-2g' || effectiveType === '2g') {
        isSlowNetwork = true;
        connectionSpeed = 'slow';
      } else if (rtt > 200 || downlink < 4 || effectiveType === '3g') {
        connectionSpeed = 'medium';
      } else {
        connectionSpeed = 'fast';
      }
      
      const speedInfo = {
        isSlowNetwork,
        connectionSpeed,
        rtt,
        downlink,
        effectiveType
      };
      
      // console.log('🌐 [Network Speed] Detectado:', speedInfo);
      notifyNetworkSpeedChange(speedInfo);
      
      return speedInfo;
    } else {
      // Fallback: usar um teste de velocidade simples
      return performSimpleSpeedTest();
    }
  } catch (error) {
    console.error('❌ [Network Speed] Erro na detecção:', error);
    return { isSlowNetwork: false, connectionSpeed: 'unknown' };
  }
};

// ✅ TESTE DE VELOCIDADE SIMPLES COMO FALLBACK
const performSimpleSpeedTest = async () => {
  try {
    const startTime = Date.now();
    const imageUrl = '/favicon.ico?' + Date.now(); // Cache bust
    
    const response = await fetch(imageUrl, { 
      cache: 'no-cache',
      mode: 'cors'
    });
    
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    // Determinar velocidade baseado na latência
    let isSlowNetwork = false;
    let connectionSpeed = 'unknown';
    
    if (duration > 2000) {
      isSlowNetwork = true;
      connectionSpeed = 'slow';
    } else if (duration > 800) {
      connectionSpeed = 'medium';
    } else {
      connectionSpeed = 'fast';
    }
    
    const speedInfo = {
      isSlowNetwork,
      connectionSpeed,
      rtt: duration,
      testType: 'simple_fetch'
    };
    
    // console.log('🌐 [Simple Speed Test] Resultado:', speedInfo);
    notifyNetworkSpeedChange(speedInfo);
    
    return speedInfo;
  } catch (error) {
    // console.error('❌ [Simple Speed Test] Erro:', error);
    return { isSlowNetwork: false, connectionSpeed: 'unknown' };
  }
};

export class ServiceWorkerManager {
  private static instance: ServiceWorkerManager;
  private registration: ServiceWorkerRegistration | null = null;
  private updateAvailable = false;
  private callbacks: { [key: string]: Function[] } = {};

  static getInstance(): ServiceWorkerManager {
    if (!ServiceWorkerManager.instance) {
      ServiceWorkerManager.instance = new ServiceWorkerManager();
    }
    return ServiceWorkerManager.instance;
  }

  // Registrar o service worker
  async register(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      // console.warn('🚫 Service Worker não suportado neste navegador');
      return false;
    }

    try {
      // console.log('🔧 Registrando Service Worker...');
      this.registration = await navigator.serviceWorker.register('/serviceWorker.js', {
        scope: '/'
      });

      // Marcar SW como ativo
      connectivityState.serviceWorkerActive = true;

      // Listeners para eventos do SW
      this.setupServiceWorkerListeners();
      
      // Global error boundary para o client
      this.setupGlobalErrorHandlers();
      
      // Setup connectivity monitoring
      this.setupConnectivityMonitoring();

      // console.log('✅ Service Worker registrado com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Falha ao registrar Service Worker:', error);
      connectivityState.serviceWorkerActive = false;
      
      // ✅ FALLBACK: continuar sem SW
      console.warn('⚠️ Continuando sem Service Worker. Funcionalidades de cache offline não estarão disponíveis.');
      this.setupConnectivityMonitoring();
      return false;
    }
  }

  // 🌐 CONNECTIVITY MONITORING SETUP
  private setupConnectivityMonitoring(): void {
    // Event listeners para mudanças de conectividade
    const handleOnline = () => {
      // console.log('🌐 [SW Integration] Online detected');
      notifyConnectivityChange(true);
      
      // ✅ DETECTAR VELOCIDADE DA REDE QUANDO VOLTAR ONLINE
      setTimeout(() => {
        detectSlowNetwork();
      }, 1000); // Aguardar 1s para a conexão estabilizar
    };

    const handleOffline = () => {
      // console.log('🌐 [SW Integration] Offline detected');
      notifyConnectivityChange(false);
      
      // ✅ LIMPAR INFORMAÇÕES DE VELOCIDADE QUANDO OFFLINE
      notifyNetworkSpeedChange({
        isSlowNetwork: false,
        connectionSpeed: 'unknown',
        rtt: 0,
        downlink: 0,
        effectiveType: 'unknown'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // ✅ LISTENER PARA MUDANÇAS NA CONEXÃO (Network Information API)
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    if (connection) {
      const handleConnectionChange = () => {
        // console.log('🌐 [SW Integration] Connection change detected');
        detectSlowNetwork();
      };
      
      connection.addEventListener('change', handleConnectionChange);
    }

    // Verificação inicial
    notifyConnectivityChange(navigator.onLine);
    
    // ✅ DETECÇÃO INICIAL DE VELOCIDADE SE ESTIVER ONLINE
    if (navigator.onLine) {
      setTimeout(() => {
        detectSlowNetwork();
      }, 2000); // Aguardar 2s após inicialização
    }
    
    // ✅ MONITORAMENTO PERIÓDICO DE VELOCIDADE (a cada 5 minutos)
    setInterval(() => {
      if (navigator.onLine) {
        const lastTest = connectivityState.lastSpeedTest;
        const now = Date.now();
        
        // Só testar se passou mais de 5 minutos desde o último teste
        if (now - lastTest > 5 * 60 * 1000) {
          detectSlowNetwork();
        }
      }
    }, 60 * 1000); // Verificar a cada minuto se precisa testar
  }

  // Configurar listeners do service worker
  private setupServiceWorkerListeners(): void {
    if (!this.registration) return;

    // Detectar atualizações
    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration && this.registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.updateAvailable = true;
            this.trigger('updateAvailable', newWorker);
            
            // Notificar callback global sobre atualização
            if (globalNotificationCallback) {
              globalNotificationCallback({
                type: 'update_available',
                timestamp: Date.now()
              });
            }
          }
        });
      }
    });

    // Listener para mensagens do SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, ...data } = event.data;
      
      switch (type) {
        case 'SW_UPDATED':
          this.trigger('swUpdated', data);
          
          // Notificar callback global
          if (globalNotificationCallback) {
            globalNotificationCallback({
              type: 'cache_updated',
              timestamp: data.timestamp
            });
          }
          break;
          
        case 'CRITICAL_ERRORS_DETECTED':
          console.warn('🚨 Erros críticos detectados:', data);
          this.trigger('criticalErrors', data);
          this.showCriticalErrorNotification(data);
          break;
          
        default:
          console.log('📨 Mensagem do SW:', event.data);
      }
    });
  }

  // Configurar handlers de erro global para o client
  private setupGlobalErrorHandlers(): void {
    // Handler para erros JavaScript
    window.addEventListener('error', (event) => {
      // ✅ FILTRAR ERROS BENIGNOS CONHECIDOS
      if (this.shouldIgnoreError(event.message)) {
        return; // Não logar erros benignos
      }

      const errorInfo = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error && event.error.stack,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };

      console.error('🔥 [Client] Global error:', errorInfo);
      
      // Enviar para o SW para tracking
      this.sendErrorToServiceWorker(errorInfo);
      
      // Disparar callback local
      this.trigger('globalError', errorInfo);
    });

    // Handler para Promise rejections não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      const reasonString = String(event.reason);
      
      // ✅ FILTRAR REJECTIONS BENIGNOS
      if (this.shouldIgnoreError(reasonString)) {
        return; // Não logar rejections benignos
      }

      const errorInfo = {
        message: event.reason && event.reason.message || String(event.reason),
        stack: event.reason && event.reason.stack,
        timestamp: Date.now(),
        url: window.location.href,
        type: 'unhandled-promise'
      };

      console.error('🔥 [Client] Unhandled promise rejection:', errorInfo);
      
      // Enviar para o SW
      this.sendErrorToServiceWorker(errorInfo);
      
      // Disparar callback local
      this.trigger('unhandledRejection', errorInfo);
      
      // Prevenir o log padrão (opcional)
      event.preventDefault();
    });

    // Handler para erros de recursos (imagens, scripts, etc.)
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        const errorInfo = {
          type: 'resource-error',
          tagName: target.tagName,
          src: (target as any).src || (target as any).href,
          message: 'Resource failed to load',
          timestamp: Date.now(),
          url: window.location.href
        };

        console.warn('📁 [Client] Resource error:', errorInfo);
        this.trigger('resourceError', errorInfo);
      }
    }, true); // Capture phase para capturar erros de recursos
  }

  /**
   * ✅ FILTRO PARA ERROS BENIGNOS - SERVICE WORKER
   * Determina se um erro deve ser ignorado por ser conhecido e benigno
   */
  private shouldIgnoreError(message: string): boolean {
    if (!message) return false;

    const benignErrors = [
      // 🟡 ResizeObserver - Muito comum no Chrome, não indica problema real
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      
      // 🟡 Script loading - Extensões do browser ou recursos externos
      'Script error',
      'Non-Error promise rejection captured',
      
      // 🟡 Network/CORS - Problemas de rede temporários
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'The operation was aborted',
      
      // 🟡 Browser extensions
      'chrome-extension:',
      'moz-extension:',
      'safari-extension:',
      
      // 🟡 Third-party widgets/analytics
      'google-analytics',
      'gtag',
      'fbq',
      'mixpanel',
      
      // 🟡 AbortController - Cancelamento normal de requests
      'AbortError',
      'The user aborted a request',
      
      // 🟡 Intersection Observer - Similar ao ResizeObserver
      'IntersectionObserver',
      
      // 🟡 WebGL context lost - Problema de hardware/driver
      'WebGL context lost',
    ];

    return benignErrors.some(benignPattern => 
      message.toLowerCase().includes(benignPattern.toLowerCase())
    );
  }

  // Enviar erro para o service worker
  private sendErrorToServiceWorker(errorInfo: any): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLIENT_ERROR',
        error: errorInfo,
        url: window.location.href,
        timestamp: Date.now()
      });
    }
  }

  // Mostrar notificação de erros críticos
  private showCriticalErrorNotification(data: any): void {
    // Criar notificação visual
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed; 
        top: 20px; 
        right: 20px; 
        background: linear-gradient(45deg, #ff6b6b, #ff5722); 
        color: white; 
        padding: 15px 20px; 
        border-radius: 10px; 
        box-shadow: 0 4px 20px rgba(255, 107, 107, 0.3);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 14px; 
        z-index: 10000;
        max-width: 350px;
        animation: slideInRight 0.3s ease;
      ">
        <div style="display: flex; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 18px; margin-right: 8px;">🚨</span>
          <strong>Erros Críticos Detectados</strong>
        </div>
        <div style="margin-bottom: 10px; opacity: 0.9;">
          ${data.message || 'Múltiplos erros detectados no sistema.'}
        </div>
        <div style="display: flex; gap: 10px;">
          <button onclick="location.reload()" style="
            background: rgba(255,255,255,0.2); 
            border: 1px solid rgba(255,255,255,0.3);
            color: white; 
            padding: 6px 12px; 
            border-radius: 5px; 
            cursor: pointer;
            font-size: 12px;
          ">
            🔄 Recarregar
          </button>
          <button onclick="this.closest('div').remove()" style="
            background: transparent; 
            border: 1px solid rgba(255,255,255,0.3);
            color: white; 
            padding: 6px 12px; 
            border-radius: 5px; 
            cursor: pointer;
            font-size: 12px;
          ">
            ✕ Fechar
          </button>
        </div>
      </div>
    `;

    // Adicionar animação CSS
    if (!document.querySelector('#critical-error-styles')) {
      const styles = document.createElement('style');
      styles.id = 'critical-error-styles';
      styles.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    // Auto-remover após 15 segundos
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 15000);
  }

  // Forçar atualização do SW
  async forceUpdate(): Promise<void> {
    if (this.registration && this.registration.waiting) {
      this.registration.waiting.postMessage('SKIP_WAITING');
      window.location.reload();
    }
  }

  // Verificar se há atualização disponível
  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }

  // Sistema de callbacks para eventos
  on(event: string, callback: Function): void {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    this.callbacks[event].push(callback);
  }

  off(event: string, callback: Function): void {
    if (this.callbacks[event]) {
      this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
    }
  }

  private trigger(event: string, data?: any): void {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in callback for event '${event}':`, error);
        }
      });
    }
  }

  // Limpar cache de erros
  async clearErrorCache(): Promise<void> {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_ERROR_CACHE'
      });
    }
  }

  // Obter status do service worker
  getStatus(): {
    registered: boolean;
    updateAvailable: boolean;
    controller: boolean;
    connectivity: any;
  } {
    return {
      registered: !!this.registration,
      updateAvailable: this.updateAvailable,
      controller: !!navigator.serviceWorker.controller,
      connectivity: getConnectivityStatus()
    };
  }
}

// Hook React para usar o Service Worker
export const useServiceWorker = () => {
  const swManager = ServiceWorkerManager.getInstance();
  
  return {
    register: () => swManager.register(),
    forceUpdate: () => swManager.forceUpdate(),
    isUpdateAvailable: () => swManager.isUpdateAvailable(),
    clearErrorCache: () => swManager.clearErrorCache(),
    getStatus: () => swManager.getStatus(),
    on: (event: string, callback: Function) => swManager.on(event, callback),
    off: (event: string, callback: Function) => swManager.off(event, callback),
    // Compatibilidade com hook existente
    getConnectivityStatus: getConnectivityStatus,
    setNotificationCallback: setNotificationCallback
  };
};

// ===================================================
// 🌍 EXPORTED FUNCTIONS FOR EXTERNAL USE
// ===================================================

export const initializeServiceWorker = async (): Promise<boolean> => {
  const manager = ServiceWorkerManager.getInstance();
  return await manager.register();
};

export const forceUpdateCheck = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      console.log('🔄 Update check forçado');
      return true;
    }
    return false;
  } catch (error) {
    console.error('❌ Erro no update check forçado:', error);
    return false;
  }
};

export const clearCache = async (): Promise<boolean> => {
  try {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    console.log('🧹 Cache limpo');
    return true;
  } catch (error) {
    console.error('❌ Erro ao limpar cache:', error);
    return false;
  }
};

export const getConnectStatus = async (): Promise<any> => {
  try {
    return new Promise((resolve) => {
      resolve(getConnectivityStatus());
    });
  } catch (error) {
    console.error('❌ Erro ao obter status de conexão:', error);
    return null;
  }
};

// ✅ NOVAS EXPORTAÇÕES PARA REDE LENTA
export const forceNetworkSpeedTest = async (): Promise<any> => {
  console.log('🌐 [Network Speed] Teste forçado iniciado');
  return await detectSlowNetwork();
};

export const getNetworkSpeedInfo = () => {
  return {
    isSlowNetwork: connectivityState.isSlowNetwork,
    connectionSpeed: connectivityState.connectionSpeed,
    lastSpeedTest: connectivityState.lastSpeedTest,
    rtt: connectivityState.rtt,
    downlink: connectivityState.downlink,
    effectiveType: connectivityState.effectiveType
  };
};

export const isSlowNetworkDetected = () => {
  return connectivityState.isSlowNetwork;
};

export default ServiceWorkerManager; 